import { LANG_SHORT } from './enums';

// Asset-type catalogue. Most text & audio artifacts are language-suffixed (`quizen`, `quizhi`,
// `quizhien`, `diagramexplanationhien`, …); every image type is language-neutral and video comes
// as `videoen`/`videohi` (no Hinglish track). The two HTML artifacts (`htmlsimulation`,
// `htmlstepsolver`) are text-kind but carry NO language suffix — one build serves every language.
//
// This module parses a concrete type into { base, lang, kind, label } rather than enumerating
// every concrete type, which keeps it correct as languages/artifacts change. A base must be
// registered below for its language suffix to be stripped — otherwise each language renders as
// its own entry with a raw label instead of collapsing into one card with EN/HI/HIN variants.

/**
 * Per-kind metadata: display label, brand colour, relative cost tier and the cost note shown
 * in the Generate dialog.
 */
export const KIND_META = {
  text: {
    label: 'Text',
    color: '#6366F1',
    costTier: 'low',
    costHint: 'Low cost — a few cents of LLM tokens per paragraph.',
    order: 0,
  },
  audio: {
    label: 'Audio',
    color: '#06B6D4',
    costTier: 'low',
    costHint: 'Low cost — cents and seconds per narration clip.',
    order: 1,
  },
  image: {
    label: 'Image',
    color: '#10B981',
    costTier: 'medium',
    costHint: 'Medium cost — one image render per eligible node.',
    order: 2,
  },
  video: {
    label: 'Video',
    color: '#F59E0B',
    costTier: 'high',
    costHint: 'High cost — dollars and minutes per clip. Use a limit to control spend.',
    order: 3,
  },
};

/** Cost tier → chip colour + label, reused by the Generate dialog. */
export const COST_TIER_META = {
  low: { label: 'Low cost', color: 'success' },
  medium: { label: 'Medium cost', color: 'warning' },
  high: { label: 'High cost', color: 'error' },
};

// Base artifacts per kind, in display order.
//
//   scope   — which node the type generates on: 'both' | 'topic' | 'paragraph' | 'chapter'. Mirrors
//             the API's scope table; a topic-only type never appears on a paragraph and vice
//             versa. 'chapter' is the quick-revision set — owned by the chapter itself, returned
//             on `document.assets`, and never gated by the priority plan.
//   revision— part of the quick-revision set: the chapter artifact, MERGED from a per-section one.
//   mergedFrom — for a chapter artifact, the topic-scope base its pages/sections come from. No
//             model runs at chapter scope: the artifact is its sections concatenated, so it costs
//             no tokens and its `prompt` reads `merged from N sections (<source>), no model call`.
//   ungated — generated for EVERY teaching section whatever it scored. A revision source has to
//             be: a section skipped for scoring low would not merely lose its own sheet, it would
//             hold up the whole chapter's merge permanently.
//   mathsOnly — skipped (never failed) on every other subject.
//   pdf     — the file is a PDF booklet rather than a single image.
//   available — false for a type the API reference documents but the deployed build does not yet
//             know. Such a type is still parsed and labelled (so a row that does appear renders
//             properly) but is never offered as a filter or a generation target, because sending
//             it as `?type=`/`types` is a 400 `unknown asset type`, not an empty result.
//   neutral — no language suffix: one artifact serves every reading language, so a `?lang` filter
//             never drops it and the reader shows no EN/HI/HIN switch for it.
//   note    — the one thing about this artifact that isn't obvious from its name; surfaced as a
//             tooltip wherever the artifact is listed.
export const TEXT_ARTIFACTS = [
  { base: 'textexplanation', label: 'Text Explanation', scope: 'both' },
  { base: 'vocabulary', label: 'Vocabulary', scope: 'topic' },
  { base: 'dictionary', label: 'Dictionary', scope: 'topic' },
  { base: 'glossary', label: 'Glossary', scope: 'topic' },
  { base: 'facts', label: 'Facts', scope: 'topic' },
  { base: 'quiz', label: 'Quiz', scope: 'topic' },
  { base: 'brainbooster', label: 'Brain Booster', scope: 'topic' },
  { base: 'diagramexplanation', label: 'Diagram Explanation', scope: 'both' },
  {
    base: 'topicnotes',
    label: 'Section Notes',
    scope: 'topic',
    ungated: true,
    note: "One section's revision sheet, written from that section's own text. Drawn for every teaching section whatever it scored.",
  },
  {
    base: 'revisionnotes',
    label: 'Revision Notes',
    scope: 'chapter',
    revision: true,
    mergedFrom: 'topicnotes',
    note: "The chapter's sheet: its sections' sheets joined in reading order. A merge, not a model call — concatenation cannot invent a formula.",
  },
  {
    base: 'htmlsimulation',
    label: 'HTML Simulation',
    scope: 'both',
    neutral: true,
    note: 'An interactive page the student plays with. Always English — one build serves every language.',
  },
  {
    base: 'htmlstepsolver',
    label: 'HTML Step Solver',
    scope: 'paragraph',
    neutral: true,
    note: 'Works one exercise question through, step by step. Exercise questions only, never a topic.',
  },
];

const AUDIO_ARTIFACTS = [
  { base: 'audioexplanation', label: 'Audio Narration', scope: 'both' },
  { base: 'diagramnarration', label: 'Diagram Narration', scope: 'both' },
  {
    base: 'topicaudio',
    label: 'Section Audio',
    scope: 'topic',
    ungated: true,
    note: "Speaks one section's own revision sheet, so the text pass has to run first.",
  },
  {
    base: 'revisionaudio',
    label: 'Revision Audio',
    scope: 'chapter',
    revision: true,
    mergedFrom: 'topicaudio',
    note: "The chapter's audiobook: its sections' narrations played end to end. Joined as raw PCM — sections that disagree on sample rate are refused rather than spliced.",
  },
];

const IMAGE_ARTIFACTS = [
  {
    base: 'diagram',
    label: 'Diagram',
    scope: 'both',
    neutral: true,
    note: 'The only image that carries its own explanation and narration.',
  },
  {
    base: 'handwrittennotes',
    label: 'Handwritten Notes',
    scope: 'both',
    neutral: true,
    ungated: true,
    note: 'Drawn for every topic whatever it scored, plus a worked page for each exercise question and in-text example. One page of the chapter’s revision booklet.',
  },
  {
    base: 'mindmap',
    label: 'Mind Map',
    scope: 'both',
    neutral: true,
    mathsOnly: true,
    note: 'Mathematics only — skipped (not failed) on every other subject. One page of the chapter’s mind-map booklet.',
  },
  {
    base: 'revisionhandwrittennotes',
    label: 'Revision Booklet',
    scope: 'chapter',
    revision: true,
    neutral: true,
    pdf: true,
    mergedFrom: 'handwrittennotes',
    note: "The sections' handwritten pages bound as a multi-page PDF — one page per teaching section, so its length is a fact about the book rather than a number chosen from a score.",
  },
  {
    base: 'revisionmindmap',
    label: 'Revision Mind Maps',
    scope: 'chapter',
    revision: true,
    neutral: true,
    pdf: true,
    mathsOnly: true,
    mergedFrom: 'mindmap',
    note: "The sections' mind maps bound as a PDF. Maths only, because its source is: a chapter with no section maps records a skip, not a failure.",
  },
];

const VIDEO_ARTIFACTS = [{ base: 'video', label: 'Video', scope: 'topic' }];

const ALL_ARTIFACTS = [...TEXT_ARTIFACTS, ...AUDIO_ARTIFACTS, ...IMAGE_ARTIFACTS, ...VIDEO_ARTIFACTS];

const KIND_OF = { text: TEXT_ARTIFACTS, audio: AUDIO_ARTIFACTS, image: IMAGE_ARTIFACTS, video: VIDEO_ARTIFACTS };

// base → { kind, label, scope, neutral, note }. Built from the lists above so the catalogue has
// exactly one definition per artifact.
const BASE_META = {};
Object.entries(KIND_OF).forEach(([kind, artifacts]) => {
  artifacts.forEach((a) => {
    BASE_META[a.base] = { kind, ...a };
  });
});

// Language-neutral (no suffix) types → their kind: every image, plus the two HTML builds.
// Video is `videoen`/`videohi` only, so it is NOT neutral.
const NEUTRAL_TYPES = {};
ALL_ARTIFACTS.forEach((a) => {
  if (a.neutral) NEUTRAL_TYPES[a.base] = BASE_META[a.base].kind;
});

// Longest suffix first so `hien` is matched before `hi`/`en`.
const LANG_SUFFIXES = ['hien', 'hi', 'en'];

const prettify = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Parse a concrete asset type into its parts.
 * @returns {{ base: string, lang: string|null, kind: string|null, label: string, neutral: boolean,
 *   scope: string|null, note: string|null, revision: boolean, pdf: boolean, available: boolean,
 *   mergedFrom: string|null, ungated: boolean, mathsOnly: boolean }}
 */
export function parseAssetType(type) {
  if (!type) {
    return {
      base: null,
      lang: null,
      kind: null,
      label: 'Unknown',
      neutral: true,
      scope: null,
      note: null,
      revision: false,
      pdf: false,
      available: true,
      mergedFrom: null,
      ungated: false,
      mathsOnly: false,
    };
  }

  const describe = (base, lang) => {
    const meta = BASE_META[base];
    return {
      base,
      lang,
      kind: meta?.kind ?? null,
      label: meta?.label || prettify(base),
      neutral: Boolean(meta?.neutral) || lang === null,
      scope: meta?.scope ?? null,
      note: meta?.note ?? null,
      revision: Boolean(meta?.revision),
      pdf: Boolean(meta?.pdf),
      // The topic-scope artifact this one is merged from, for a chapter artifact.
      mergedFrom: meta?.mergedFrom ?? null,
      ungated: Boolean(meta?.ungated),
      mathsOnly: Boolean(meta?.mathsOnly),
      // Unknown bases are assumed available: a type this catalogue has never heard of is far
      // more likely to be one the backend added than one it rejects.
      available: meta?.available !== false,
    };
  };

  // A language-neutral type is never suffix-stripped — `diagram` must not lose an "en" it
  // doesn't have, and `htmlstepsolver` must survive a naive trailing-suffix trim.
  if (NEUTRAL_TYPES[type]) return { ...describe(type, null), neutral: true };

  // Strip a language suffix only when the remaining base is a known artifact.
  for (const suf of LANG_SUFFIXES) {
    if (type.endsWith(suf) && type.length > suf.length) {
      const candidate = type.slice(0, -suf.length);
      if (BASE_META[candidate]) return { ...describe(candidate, suf), neutral: false };
    }
  }

  // No suffix (or unknown base): treat as an un-suffixed known base, else best-effort.
  return describe(type, null);
}

/** Base type (language stripped), e.g. `quizhien` → `quiz`. Drives per-type rendering. */
export const assetBaseType = (type) => parseAssetType(type).base;

/** Friendly base label for an asset type (no language), e.g. `quizhien` → "Quiz". */
export const assetTypeLabel = (type) => parseAssetType(type).label;

/** The one non-obvious thing about an artifact, or null. Rendered as a tooltip. */
export const assetTypeNote = (type) => parseAssetType(type).note;

/** Which node an artifact generates on: 'both' | 'topic' | 'paragraph' | null. */
export const assetTypeScope = (type) => parseAssetType(type).scope;

/**
 * The base artifacts a kind produces (for the Generate dialog "produces" list).
 * Types the deployed backend does not know are left out — offering one would only ever produce a
 * `400 unknown asset type`.
 */
export function baseArtifactsForKind(kind) {
  return (KIND_OF[kind] || []).filter((a) => a.available !== false);
}

/**
 * The quick-revision set: the four chapter-scope artifacts, in reading order — the written sheet,
 * the audiobook, then the two booklets.
 *
 * **Every one of them is a merge.** No model runs at chapter scope. Each is its per-section
 * counterpart concatenated in reading order, and that split is the whole design: a single call
 * handed forty paragraphs keeps the headings, flattens the middle, and states results the chapter
 * never established — fluent, well-formatted and wrong in ways only someone who knows the chapter
 * would catch. A section fits. Concatenation cannot invent a formula.
 *
 * They are owned by the chapter itself, so they arrive on `document.assets` rather than under a
 * topic or paragraph. A merge whose sections are not all ready records a **skip** naming the first
 * one it is waiting on — a partial chapter sheet would read as complete while missing a third of
 * the chapter. The **Exercises** topic is excluded: a question bank is not what a student opens
 * the night before.
 */
export const REVISION_ARTIFACTS = [
  'revisionnotes',
  'revisionaudio',
  'revisionhandwrittennotes',
  'revisionmindmap',
]
  .map((base) => BASE_META[base])
  .filter(Boolean);

/** True when a type belongs to the quick-revision (chapter-scope) set. */
export const isRevisionType = (type) => parseAssetType(type).revision;

/**
 * True when this artifact's file is a PDF rather than an image — currently only the handwritten
 * revision booklet, which is `kind: 'image'` but `application/pdf`, so an `<img>` would render
 * nothing. The asset's own `mimeType` is the better signal when it is present; this covers the
 * moment before the row is loaded.
 */
export const assetTypeIsPdf = (type) => parseAssetType(type).pdf;

/**
 * The topic-scope artifact a chapter artifact is merged from, or null.
 * `revisionnotesen` → `topicnotes`. Lets the UI name the source a chapter artifact is waiting on
 * when it hasn't been built yet, which is the actual next step rather than "not generated".
 */
export const assetTypeMergedFrom = (type) => parseAssetType(type).mergedFrom;

/** Short language badge, e.g. 'hien' → 'HIN'. */
export const langShort = (lang) => (lang ? LANG_SHORT[lang] || String(lang).toUpperCase() : '');
